import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMemo } from 'react';

interface CommentModeratorProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Allows admins and any user granted comment approve/reject permission.
 */
const CommentModeratorProtectedRoute = ({
  children,
}: CommentModeratorProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user, getRoleBasedRedirect } = useAuth();

  const canModerate = useMemo(() => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return Boolean(user.can_moderate_comments || user.canModerateComments);
  }, [user]);

  const redirectPath = useMemo(() => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/dashboard';
    return getRoleBasedRedirect(user.role, user.active_profile_type);
  }, [user, getRoleBasedRedirect]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F7EC] to-[#E8F1D1] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#7E8D4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6b6358]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canModerate) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default CommentModeratorProtectedRoute;
